import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import { fromLonLat } from 'ol/proj';
import { setByName, getByName, normalizeName } from './utils';
import { createCurvedSegmentCoordinates, getArrowPlacementAtFraction } from './geometry';

export async function loadRouteSequence() {
  try {
    const response = await fetch('./route.json');
    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return (data || []).map((item, index) => ({
      location: item.location,
      order: Number.isFinite(item.order) ? item.order : index + 1
    }));
  } catch (error) {
    console.warn('route.json not found or invalid; falling back to implicit order from locations.json');
    return [];
  }
}

export async function loadLocations(
  vectorSource,
  connectionVectorSource,
  locationsByName,
  locationFeaturesByName,
  referencedLocationsByName,
  referencedFeaturesByName
) {
  try {
    const response = await fetch('./locations.json');
    const data = await response.json();
    const routeSequence = await loadRouteSequence();
    const effectiveRoute = routeSequence.length > 0
      ? routeSequence
      : data.map((item, index) => ({ location: item.name, order: index + 1 }));

    const routeVisitsByLocation = {};
    effectiveRoute.forEach((entry, index) => {
      const key = normalizeName(entry.location);
      if (!routeVisitsByLocation[key]) {
        routeVisitsByLocation[key] = [];
      }
      routeVisitsByLocation[key].push({
        order: Number.isFinite(entry.order) ? entry.order : index + 1
      });
    });

    const features = [];
    const connectionFeatures = [];
    const locationCoordsByName = {};

    data.forEach((item, index) => {
      setByName(locationsByName, item);

      const coord = fromLonLat([item.lon, item.lat]);
      locationCoordsByName[normalizeName(item.name)] = coord;

      const explicitVisits = Array.isArray(item.visits) ? item.visits : [];
      const normalizedExplicitVisits = explicitVisits
        .map((visit, visitIndex) => ({
          order: Number.isFinite(visit.order) ? visit.order : visitIndex + 1,
          chapter: visit.chapter || '',
          pages: visit.pages || '',
          note: visit.note || ''
        }))
        .sort((a, b) => a.order - b.order);

      const routeVisits = routeVisitsByLocation[normalizeName(item.name)] || [];
      const visits = normalizedExplicitVisits.length > 0
        ? normalizedExplicitVisits
        : (routeVisits.length > 0
          ? routeVisits.map((routeVisit, routeIndex) => ({
              order: routeVisit.order,
              chapter: item.chapter || '',
              pages: item.pages || '',
              note: routeIndex > 0 ? 'Revisit' : ''
            }))
          : [{
              order: index + 1,
              chapter: item.chapter || '',
              pages: item.pages || '',
              note: ''
            }]);

      const primaryVisit = visits[0] || {};
      const relatedReferencedLocations = item.relatedReferencedLocations || item.relatedLocations || [];
      const feature = new Feature({
        geometry: new Point(coord),
        type: 'location',
        name: item.name,
        step: primaryVisit.order || index + 1,
        description: item.description,
        chapter: primaryVisit.chapter || item.chapter || '',
        pages: primaryVisit.pages || item.pages || '',
        visits,
        links: item.links,
        images: item.images,
        relatedLocations: item.relatedLocations || [],
        relatedReferencedLocations,
        relatedThemes: item.relatedThemes || [],
        relatedPeople: item.relatedPeople || []
      });
      features.push(feature);
      setByName(locationFeaturesByName, {
        name: item.name,
        feature
      });

      // Create connection lines to referenced locations
      if (relatedReferencedLocations.length > 0) {
        relatedReferencedLocations.forEach(refName => {
          const referencedLocation = getByName(referencedLocationsByName, refName);
          if (referencedLocation) {
            const refCoord = fromLonLat([referencedLocation.lon, referencedLocation.lat]);
            const connectionFeature = new Feature({
              geometry: new LineString([coord, refCoord]),
              locationName: item.name,
              referencedLocationName: refName
            });
              connectionFeatures.push(connectionFeature);
          }
        });
      }
    });

      if (connectionFeatures.length > 0) {
        connectionVectorSource.addFeatures(connectionFeatures);
      }

    const orderedRouteEntries = effectiveRoute
      .sort((a, b) => a.order - b.order)
      .map(entry => ({
        ...entry,
        coord: locationCoordsByName[normalizeName(entry.location)]
      }))
      .filter(entry => entry.coord);

    // Add curved, labeled route segments with arrowheads.
    for (let i = 0; i < orderedRouteEntries.length - 1; i += 1) {
      const fromEntry = orderedRouteEntries[i];
      const toEntry = orderedRouteEntries[i + 1];
      const curveCoords = createCurvedSegmentCoordinates(fromEntry.coord, toEntry.coord, i);
      const arrowPlacement = getArrowPlacementAtFraction(curveCoords, 0.25);

      const segmentFeature = new Feature({
        geometry: new LineString(curveCoords),
        routeSegment: true,
        routeLabel: `${fromEntry.order}`,
        arrowCoord: arrowPlacement.coord,
        arrowAngle: arrowPlacement.angle
      });
      features.push(segmentFeature);
    }

    vectorSource.addFeatures(features);
    return orderedRouteEntries;
  } catch (error) {
    console.error('Error loading locations:', error);
    return [];
  }
}

export async function loadReferencedLocations(referencedVectorSource, referencedLocationsByName, referencedFeaturesByName) {
  try {
    const response = await fetch('./referenced_locations.json');
    const data = await response.json();
    const features = [];

    data.forEach(item => {
      setByName(referencedLocationsByName, item);
      const feature = new Feature({
        geometry: new Point(fromLonLat([item.lon, item.lat])),
        type: 'referencedLocation',
        name: item.name,
        description: item.description,
        links: item.links,
        images: item.images,
        pages: item.pages || [],
        relatedLocations: item.relatedLocations || [],
        relatedReferencedLocations: item.relatedReferencedLocations || [],
        relatedThemes: item.relatedThemes || [],
        relatedPeople: item.relatedPeople || []
      });
      features.push(feature);
      setByName(referencedFeaturesByName, {
        name: item.name,
        feature
      });
    });

    referencedVectorSource.addFeatures(features);
  } catch (error) {
    console.error('Error loading referenced locations:', error);
  }
}

export async function loadThemes(themesByName) {
  try {
    const response = await fetch('./themes.json');
    const data = await response.json();
    data.forEach(item => setByName(themesByName, item));
  } catch (error) {
    console.error('Error loading themes:', error);
  }
}

export async function loadPeople(peopleByName) {
  try {
    const response = await fetch('./people.json');
    const data = await response.json();
    data.forEach(item => setByName(peopleByName, item));
  } catch (error) {
    console.error('Error loading people:', error);
  }
}
